import { gql, TypedDocumentNode } from '@apollo/client';

export interface StageSummary {
    wellhead: string;
    preparing: number;
    running: number;
    paused: number;
    completed: number;
    remaining: number;
}

export interface StageDetail {
    stageGroupId: number | null;
    stageSheetId: number | null;
    jobStageId: number | null;
    stage: number | null;
    overallStage: number | null;
    startDate: string | null;
    endDate: string | null;
    wellhead: string | null;
    status: string | null;
    comments: string | null;
    fracType: string | null;
}

export interface StageDetailsResponse {
    isLastItem: boolean | null;
    isPagingRequired: boolean | null;
    wellName: string | null;
    jobStatus: string | null;
    isBellyDump: boolean | null;
    stageDetails: (StageDetail | null)[] | null;
}

export interface StageTicket {
    id: number | null;
    loadId: number | null;
    sandTicket: string | null;
    isLoadCompleted: boolean | null;
    isPartial: boolean | null;
    sandTicketWeight: number | null;
    sandTicketWeightRemaining: number | null;
    sandTicketLink: string | null;
}

export interface StageSheetBox {
    stageSheetProppantId: number | null;
    stageSheetBoxId: number | null;
    station: string | null;
    runOrder: number | null;
    boxDesc: string | null;
    boxWeight: number | null;
    boxWeightRemaining: number | null;
    isOOS: boolean | null;
    proppant: string | null;
    location: string | null;
    sandPo: string | null;
    sandTicket: string | null;
    loadId: number | null;
    sandTicketLink: string | null;
    accumulatedWeight: number | null;
    currentProppantWeight: number | null;
    isPartial: string | null;
    loadWeight: number | null;
    isCompleted: boolean | null;
    lofaOrigin: string | null;
    stageSheetBellyDump: (StageTicket | null)[] | null;
}

export interface StageProppant {
    stageSheetProppantId: number | null;
    proppantName: string | null;
    totalWeightNeeded: number | null;
    sandPumped: number | null;
    totalBoxWeight: number | null;
}

export const GET_STAGE_SUMMARY: TypedDocumentNode<{
    getStageSummary: (StageSummary | null)[] | null;
}, { jobId: number }> = gql`
    query GetStageSummary($jobId: Int) {
        getStageSummary(jobId: $jobId) {
            wellhead preparing running paused completed remaining
        }
    }
`;

export const GET_STAGE_DETAILS: TypedDocumentNode<{
    getStageDetails: StageDetailsResponse | null;
}, { input: { jobId: number; isCompleted: boolean; pageNum: number; wellHead: string } }> = gql`
    query GetStageDetails($input: GetStageDetailsInput) {
        getStageDetails(input: $input) {
            isLastItem isPagingRequired wellName jobStatus isBellyDump
            stageDetails {
                stageGroupId stageSheetId jobStageId stage overallStage
                startDate endDate wellhead status comments fracType
            }
        }
    }
`;

export const GET_DETAILS_FOR_STAGE_SHEET: TypedDocumentNode<{
    getDetailsForStageSheet: {
        wellheadStages: string | null;
        jobId: number;
        wellName: string;
        stageStatus: ({ stageId: number | null; status: string | null; wellHead: string | null } | null)[] | null;
    } | null;
}, { stageSheetId: number }> = gql`
    query GetDetailsForStageSheet($stageSheetId: Int) {
        getDetailsForStageSheet(stageSheetId: $stageSheetId) {
            wellheadStages jobId wellName
            stageStatus { stageId status wellHead }
        }
    }
`;

export const GET_STAGE_SHEET: TypedDocumentNode<{
    getStageSheet: {
        propDetails: (StageProppant | null)[] | null;
        details: (StageSheetBox | null)[] | null;
    } | null;
}, { stageSheetId: number }> = gql`
    query GetStageSheet($stageSheetId: Int) {
        getStageSheet(stageSheetId: $stageSheetId) {
            propDetails {
                stageSheetProppantId proppantName totalWeightNeeded sandPumped totalBoxWeight
            }
            details {
                stageSheetProppantId stageSheetBoxId station runOrder boxDesc boxWeight
                boxWeightRemaining isOOS proppant location sandPo sandTicket loadId sandTicketLink
                accumulatedWeight currentProppantWeight isPartial loadWeight isCompleted lofaOrigin
                stageSheetBellyDump {
                    id loadId sandTicket isLoadCompleted isPartial
                    sandTicketWeight sandTicketWeightRemaining sandTicketLink
                }
            }
        }
    }
`;